import { createAsyncThunk, isFulfilled, isPending } from '@reduxjs/toolkit';
import axios from 'axios';

import { defaultValue, ITruckOdometer } from 'app/shared/model/truck-odometer.model';
import { createEntitySlice, EntityState, IQueryParams, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { cleanEntity } from 'app/shared/util/entity-utils';

const initialState: EntityState<ITruckOdometer> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

const apiUrl = 'api/truck-odometers';

export const getEntities = createAsyncThunk('truckOdometer/fetch_entity_list', async ({ page, size, sort }: IQueryParams) => {
  const requestUrl = `${apiUrl}${sort ? `?page=${page}&size=${size}&sort=${sort}&` : '?'}cacheBuster=${new Date().getTime()}`;
  return axios.get<ITruckOdometer[]>(requestUrl);
});

export const getEntity = createAsyncThunk(
  'truckOdometer/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<ITruckOdometer>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const getOdometerByTruckImplementId = createAsyncThunk(
  'truckOdometer/fetch_by_truck_implement',
  async (truckImplementId: string | number) => {
    const requestUrl = `${apiUrl}/truck-implement/${truckImplementId}`;
    return axios.get<ITruckOdometer>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const getOdometerSummaries = createAsyncThunk(
  'truckOdometer/fetch_odometer_summaries',
  async (truckImplementIds: number[]) => {
    const requestUrl = `${apiUrl}/summaries`;
    return axios.post<ITruckOdometer[]>(requestUrl, truckImplementIds);
  },
  { serializeError: serializeAxiosError }
);

export const createEntity = createAsyncThunk(
  'truckOdometer/create_entity',
  async (entity: ITruckOdometer, thunkAPI) => {
    const result = await axios.post<ITruckOdometer>(apiUrl, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateEntity = createAsyncThunk(
  'truckOdometer/update_entity',
  async (entity: ITruckOdometer, thunkAPI) => {
    const result = await axios.put<ITruckOdometer>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const partialUpdateEntity = createAsyncThunk(
  'truckOdometer/partial_update_entity',
  async (entity: ITruckOdometer, thunkAPI) => {
    const result = await axios.patch<ITruckOdometer>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteEntity = createAsyncThunk(
  'truckOdometer/delete_entity',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<ITruckOdometer>(requestUrl);
    return result;
  },
  { serializeError: serializeAxiosError }
);

// Slice

export const TruckOdometerSlice = createEntitySlice({
  name: 'truckOdometer',
  initialState,
  reducers: {
    reset(state) {
      state.updateSuccess = false;
      state.loading = false;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(getOdometerByTruckImplementId.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data || null;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(getEntities, getOdometerSummaries), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(createEntity, updateEntity, partialUpdateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getEntities, getEntity, getOdometerByTruckImplementId, getOdometerSummaries), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
        state.entity = {};
      })
      .addMatcher(isPending(createEntity, updateEntity, partialUpdateEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = TruckOdometerSlice.actions;

// Reducer
export default TruckOdometerSlice.reducer;
